import { appNameFromPath, pathForApp } from "../runtime/stage-router.js";
import { applyBrandEconShine } from "./brand-shine.js";
import { OWNER_STAGE_LABELS, OWNER_STAGE_NAMES } from "../stages/owner-stage-routes.js";
import econLogoUrl from "../skin/brand/econ-logo.png?url";
import nodeMarkUrl from "../skin/brand/node-mark.jpg?url";
import { escapeHtml } from "../util/escape-html.js";
import { currentDeviceLabel } from "./current-device.js";
/**
 * The five first-party owner stages, always present — same source pmenu.ts's
 * `OWNER_NAV_ITEMS` derives from (`owner-stage-routes.ts`), so the two nav surfaces can't
 * drift out of sync with each other or with `app-shell.ts`'s `OWNER_STAGE_TAGS` mount map.
 * These are NOT registry-backed apps (`stages.installed.v1` never carries rows for them —
 * see `client/kernel/src/stages/stage-registry-service.ts`'s `GET /api/v2/node-apps/
 * ui-stages`-backed catalog, which only ever lists real installable node-app stages); they
 * are baked-in kernel-native dashboards `app-shell.ts#renderPath` mounts unconditionally,
 * matching pmenu.ts's own explicit, already-shipped design decision for the identical five
 * links ("always present ... regardless of what's installed" — see that file's class doc).
 */
const TB_LINKS = OWNER_STAGE_NAMES.map((name) => ({
    label: OWNER_STAGE_LABELS[name],
    nav: name,
}));
const DEFAULT_DEVICE_LABEL = currentDeviceLabel(undefined);
/**
 * The original skin's `.tb-center` primary nav is fixed to the five owner stages
 * (home/wallet/friends/agents/profile, `TB_LINKS` above) — always offered, mirroring
 * pmenu.ts's primary list (see `TB_LINKS`'s own doc comment for why that's correct rather
 * than a gap: those five aren't registry-backed apps at all).
 *
 * #1527: the mobile topbar used to be the ONLY shell nav surface with no route at all to
 * the *dynamic*, registry-installed stage apps pmenu's desktop "Stages + more" section
 * exposes — a real reachability gap on mobile, not just a cosmetic one. `items` (below)
 * carries that same `stages.installed.v1`-backed `NavItem[]` (the exact list app-shell.ts
 * already computes and feeds to `<node-shell-pmenu>`) and renders it as a second `.tb-link`
 * group, appended after the fixed five, only when non-empty. Because it's sourced directly
 * from the installed-stage registry, it can only ever offer navigation to something that
 * actually is installed — which is the one place in this nav where "installed" is a
 * coherent, testable constraint at all (see the five-link doc comment above for why it
 * isn't for those).
 *
 * `.tb-left` carries the original's `.brand-stack` (`SKIN/index.html` ~lines 171-184): the
 * always-visible `.node-mark`, plus a hover-revealed `.brand-reveal` (the "ON" wordmark
 * clip-in + the animated `.brand-econ-shine` EconV1 gradient, both choreographed by
 * vendored `base.css` — `.brand-reveal`/`.brand-on-letter`/`.brand-econ-wrap` transitions,
 * `@keyframes brandEconFlow`, and the `prefers-reduced-motion` guard all already vendored,
 * reused as-is). `brand-shine.ts` ports the one piece of behavior the CSS can't do on its
 * own: pointing `.brand-econ-shine`'s mask at the pre-baked transparent silhouette asset
 * (see that module's doc comment for why).
 */
export class NodeShellTopbarElement extends HTMLElement {
    #client;
    /** Real current-device label (see `pmenu.ts`'s identical field doc — `app-shell.ts`
     * resolves both from the same `current-device.ts` call and feeds this the same string). */
    #deviceLabel = DEFAULT_DEVICE_LABEL;
    #items = [];
    #state;
    #currentPath = "/settings";
    connectedCallback() {
        this.addEventListener("click", this.#handleClick);
        this.render();
    }
    disconnectedCallback() {
        this.removeEventListener("click", this.#handleClick);
    }
    set state(value) {
        this.#state = value;
        this.render();
    }
    set client(value) {
        this.#client = value;
        this.render();
    }
    get client() {
        return this.#client;
    }
    /** Real device label from `current-device.ts` — see the class field's own doc for the
     * fallback contract. */
    set currentDeviceName(value) {
        this.#deviceLabel = value;
        this.render();
    }
    set currentPath(value) {
        this.#currentPath = value;
        this.render();
    }
    /** The same registry-installed `NavItem[]` app-shell.ts feeds to `<node-shell-pmenu>`
     * (`stages.installed.v1`, via `navItems()`) — see the class doc for why the mobile
     * topbar needs its own copy of this list. */
    set items(value) {
        this.#items = value;
        this.render();
    }
    render() {
        const activeNav = appNameFromPath(this.#currentPath);
        const deviceLabel = this.#deviceLabel;
        const initials = deviceInitials(deviceLabel);
        this.innerHTML = `
      <div class="tb-left">
        <button class="collapse-btn" type="button" data-collapse-rail aria-label="Collapse rail" title="Collapse rail">
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 3.5 L5 7 L8.5 10.5"/></svg>
        </button>
        <div class="brand-stack" id="brandStack" data-navigate-home title="Home">
          <img class="node-mark" src="${nodeMarkUrl}" alt="Node">
          <div class="brand-reveal" aria-hidden="true">
            <span class="brand-on">
              <span class="brand-on-letter">O</span><span class="brand-on-letter">N</span>
            </span>
            <span class="brand-econ-wrap">
              <img class="brand-econ-logo" src="${econLogoUrl}" alt="Economy V1">
              <span class="brand-econ-shine" aria-hidden="true"></span>
            </span>
          </div>
        </div>
      </div>
      <div class="tb-center">
        ${TB_LINKS.map((link) => `
          <button
            class="tb-link${link.nav === activeNav ? " active" : ""}"
            data-nav="${link.nav}"
            aria-current="${link.nav === activeNav ? "page" : "false"}"
            type="button"
          >${escapeHtml(link.label)}</button>
        `).join("")}
        ${this.#items.map((item) => `
          <button
            class="tb-link${item.path === this.#currentPath ? " active" : ""}"
            data-path="${item.path}"
            data-tb-stage-link
            aria-current="${item.path === this.#currentPath ? "page" : "false"}"
            type="button"
          >${escapeHtml(item.label)}</button>
        `).join("")}
      </div>
      <div class="tb-right">
        <node-connectivity-badge></node-connectivity-badge>
        <node-sync-indicator></node-sync-indicator>
        <node-sync-center></node-sync-center>
        <div class="user-chip" data-navigate-profile role="button" tabindex="0" title="Profile">
          <span class="avatar">${initials}</span>
          <span class="u-name">${escapeHtml(deviceLabel)}</span>
        </div>
      </div>
    `;
        const badge = this.querySelector("node-connectivity-badge");
        if (badge) {
            badge.state = this.#state;
        }
        const syncIndicator = this.querySelector("node-sync-indicator");
        if (syncIndicator) {
            syncIndicator.client = this.#client;
        }
        const syncCenter = this.querySelector("node-sync-center");
        if (syncCenter) {
            syncCenter.client = this.#client;
        }
        applyBrandEconShine(this);
    }
    #handleClick = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }
        const collapseBtn = target.closest("[data-collapse-rail]");
        if (collapseBtn) {
            this.closest(".shell")?.classList.toggle("collapsed");
            return;
        }
        const link = target.closest("[data-nav]");
        if (link) {
            this.#navigate(pathForApp(link.dataset.nav));
            return;
        }
        // The dynamic, registry-installed group (`items`, see the class doc) — already a full
        // path (`NavItem.path`, sourced from `stages.installed.v1`), unlike `[data-nav]` above
        // which is one of the five fixed owner-stage short names resolved via `pathForApp`.
        const stageLink = target.closest("[data-path]");
        if (stageLink) {
            this.#navigate(stageLink.dataset.path);
            return;
        }
        if (target.closest("[data-navigate-home]")) {
            this.#navigate(pathForApp("home"));
            return;
        }
        if (target.closest("[data-navigate-profile]")) {
            this.#navigate(pathForApp("profile"));
        }
    };
    #navigate(path) {
        this.dispatchEvent(new CustomEvent("navigate", {
            bubbles: true,
            composed: true,
            detail: { path },
        }));
    }
}
function deviceInitials(label) {
    const initials = label
        .split(/\s+/u)
        .filter((word) => word.length > 0)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("");
    return initials || "N";
}
//# sourceMappingURL=topbar.js.map